import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const printPdfRingkasanPenjualan = {
  /**
   * Check if we need a new page for the next content
   * @param {Object} doc - jsPDF document instance
   * @param {number} contentHeight - Height of the content to be added (in mm)
   * @param {number} currentY - Current Y position on the page
   * @returns {Object} Object containing newY and pageBreak flag
   */
  checkPageBreak(doc, contentHeight, currentY) {
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginBottom = 20; // Bottom margin in mm

    if (currentY + contentHeight > pageHeight - marginBottom) {
      doc.addPage();
      return { newY: 20, pageBreak: true };
    }
    return { newY: currentY, pageBreak: false };
  },

  /**
   * Generate sales by category PDF report
   * @param {Object} options - Report options
   * @param {string} options.companyName - Company name
   * @param {string} options.companyAddress - Company address
   * @param {string} options.companyPhone - Company phone
   * @param {Array} report - Report data
   * @param {boolean} showQty - Whether to show quantity
   * @param {boolean} showRevenue - Whether to show revenue
   */
  generateReport(reports, options = {}) {
    const defaultOptions = {
      companyName: process.env.VUE_APP_STORE_NAME || 'Nama Toko',
      companyAddress: process.env.VUE_APP_STORE_ADDRESS || 'Alamat Toko',
      companyPhone: process.env.VUE_APP_STORE_PHONE || 'Telepon',
      startDate: options.startDate || '2023-01-01',
      endDate: options.endDate || '2023-12-31',
      title: 'Laporan Ringkasan Penjualan',
      ...options
    };

    // Create new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Add header
    this.addHeader(doc, defaultOptions, true);

    // Add title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('LAPORAN RINGKASAN PENJUALAN', 105, 30, { align: 'center' });

    // Add print date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Dicetak: ${new Date().toLocaleString('id-ID')}`, 14, 40);

    let startY = 50;

    // Add each category
    reports?.forEach((report, index) => {
      // Add summary table with page break check
      if (report?.summaryTableData) {
        startY = index === 0 ? 45 : startY; // Reduced from 20 to 10 for better spacing
        const summaryTableData = report?.summaryTableData;

        const tableHeight = (summaryTableData.length + 1) * 8 + 10; // Calculate table height
        const tablePageCheck = this.checkPageBreak(doc, tableHeight, startY);

        // Call with custom options
        startY = this.addSummaryTable(doc, summaryTableData, 14, tablePageCheck.newY, {
          columnWidths: report?.summaryTableColumnWidths, // Diperlebar dari [20, 30, 30, 30, 30]
          headers: report?.summaryTableHeader,
          fontSize: 9,
          showHeader: report?.options?.showHeader || report?.summaryTableHeader.length > 0,
          ...report?.options
        });

        // Add page break if we're too close to the bottom for the next category
        if (startY > 250 && index < reports.length - 1) {
          doc.addPage();
          this.addHeader(doc, defaultOptions);
          startY = 30; // Reset Y position for new page
        }
      }

    });

    // Add footer
    this.addFooter(doc);

    // Save the PDF
    const fileName = `${defaultOptions.title}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  },

  /**
   * Add header to the PDF
   * @param {Object} doc - jsPDF document instance
   * @param {Object} options - Header options
   */
  addHeader(doc, options, isFirstPage = false) {
    const startY = isFirstPage ? 15 : 15; // Start higher on subsequent pages
    // Company Name
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(options.companyName, 105, 10, { align: 'center' });

    // Company Address
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(options.companyAddress, 105, 15, { align: 'center' });

    // Company Phone
    doc.text(options.companyPhone, 105, 20, { align: 'center' });

    // Date Range on left side
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Periode: ${options.startDate} s/d ${options.endDate}`, 14, startY + 5);

    // Page number on right side for all pages
    const pageCount = doc.internal.getNumberOfPages();
    doc.text(`Halaman ${pageCount}`, 190, startY + 5, { align: 'right' });

    // Line separator
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(14, 25, 200, 25);
  },

  /**
   * Add summary table to the PDF
   * @param {Object} doc - jsPDF document instance
   * @param {Array} data - Summary data array
   * @param {number} x - X position
   * @param {number} y - Y position
   */
  addSummaryTable(doc, data, x, y, options = {}) {
    try {
      const startX = x;
      let startY = y;
      const rowHeight = options.rowHeight || 12;
      const fontSize = options.fontSize || 10;
      const textColor = options.textColor || [0, 0, 0];
      const boldLastRow = options.boldLastRow !== false;
      const showHeader = options.showHeader !== false;

      // Add title if provided
      if (options.title) {
        const titleOptions = {
          fontSize: options.titleFontSize || fontSize + 2,
          align: options.titleAlign || 'left',
          color: options.titleColor || textColor,
          marginBottom: options.titleMarginBottom || 5
        };

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(titleOptions.fontSize);
        doc.setTextColor(...titleOptions.color);

        doc.text(
          options.title,
          titleOptions.align === 'center' ? startX + (tableWidth / 2) : startX,
          startY + titleOptions.fontSize,
          { align: titleOptions.align }
        );

        startY += titleOptions.fontSize + titleOptions.marginBottom;

        // Reset to normal font for the table
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fontSize);
        doc.setTextColor(...textColor);
      }

      // Default column widths if not provided
      const defaultColumnWidths = [100, 80, 60, 60]; // Default widths for up to 4 columns
      const columnWidths = options.columnWidths || defaultColumnWidths;

      // Calculate total table width
      const tableWidth = columnWidths.reduce((sum, width) => sum + width, 0);

      // Default padding
      const padding = {
        left: options.padding?.left || 0,
        right: options.padding?.right || 0,
        top: options.padding?.top || 0,
        bottom: options.padding?.bottom || 0
      };

      // Border style options
      const borderStyle = {
        // Outer borders
        top: options.border?.top ?? false,
        right: options.border?.right ?? false,
        bottom: options.border?.bottom ?? false,
        left: options.border?.left ?? false,
        // Inner borders
        innerV: options.border?.innerV ?? false, // vertical inner borders
        innerH: options.border?.innerH ?? false  // horizontal inner borders
      };

      // Cell borders - can be set per cell
      const cellBorders = options.cellBorders || {};

      // Validate input data
      if (!Array.isArray(data) || data.length === 0) {
        console.warn('No data provided for summary table');
        return y;
      }

      // Set initial font
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontSize);
      doc.setTextColor(...textColor);

      // Draw header if enabled
      if (showHeader && options.headers) {
        doc.setFillColor(255, 255, 255);
        doc.rect(startX, startY, tableWidth, rowHeight, 'F');

        // Draw header borders
        if (borderStyle.top) {
          doc.setDrawColor(0, 0, 0);
          doc.line(startX, startY, startX + tableWidth, startY);
        }

        // Draw header cells
        options.headers.forEach((header, colIndex) => {
          // Use the same calculation as in the body cells
          const xPos = startX + columnWidths.slice(0, colIndex).reduce((sum, w) => sum + w, 0);
          const width = columnWidths[colIndex];
          const align = colIndex === 0 ? 'left' : 'right';

          // Draw cell background
          doc.setFillColor(255, 255, 255);
          doc.rect(xPos, startY, width, rowHeight, 'F');

          // Draw cell text
          doc.setFont('helvetica', 'bold');
          doc.text(
            header,
            xPos + (align === 'left' ? padding.left : width - padding.right),
            startY + rowHeight / 2 + 3,
            {
              align,
              baseline: 'middle',
              maxWidth: width - (padding.left + padding.right) // Prevent text overflow
            }
          );

          // Draw right border if not the last column
          // if (colIndex < options.headers.length - 1) {
          //   doc.setDrawColor(200, 200, 200);
          //   doc.line(xPos + width, startY, xPos + width, startY + rowHeight);
          // }

          // Add bottom border to all header cells
          doc.setDrawColor(0, 0, 0); // Black color for bottom border
          doc.line(xPos, startY + rowHeight, xPos + width, startY + rowHeight);
        });
      }

      // Draw rows
      data.forEach((row, rowIndex) => {
        const currentY = startY + (showHeader ? (rowIndex + 1) * rowHeight : rowIndex * rowHeight);
        const isLastRow = rowIndex === data.length - 1;

        // Set font weight for last row if boldLastRow is true
        if (isLastRow && boldLastRow) {
          doc.setFont('helvetica', 'bold');
        }

        // Draw row background for even rows
        // if (rowIndex % 2 === 0) {
        //   doc.setFillColor(250, 250, 250);
        //   doc.rect(startX, currentY, tableWidth, rowHeight, 'F');
        // }

        // Draw cells
        row.forEach((cell, colIndex) => {
          // Calculate x position by summing up all previous column widths
          const xPos = startX + columnWidths.slice(0, colIndex).reduce((sum, w) => sum + w, 0);
          const width = columnWidths[colIndex];
          const align = colIndex === 0 ? 'left' : 'right';
          const cellPadding = cell.padding || padding;

          // Apply cell-specific styles
          const cellStyle = {
            bgColor: cell.bgColor || [255, 255, 255], // Removed zebra striping for now
            textColor: cell.textColor || textColor,
            align: cell.align || align,
            bold: cell.bold || false
          };

          // Draw cell background
          doc.setFillColor(...cellStyle.bgColor);
          doc.rect(xPos, currentY, width, rowHeight, 'F');

          // Draw cell text
          doc.setTextColor(...cellStyle.textColor);
          doc.setFont('helvetica', cellStyle.bold ? 'bold' : 'normal');

          let displayValue = '';
          if (cell.value !== undefined) {
            if (cell.type === 'currency') {
              displayValue = this.formatCurrency(cell.value);
            } else if (cell.type === 'number') {
              displayValue = cell.value.toLocaleString('id-ID');
            } else {
              displayValue = String(cell.value);
            }
          } else if (cell.text !== undefined) {
            displayValue = String(cell.text);
          }

          // Adjust text position to prevent overflow
          const textX = cellStyle.align === 'left'
            ? xPos + cellPadding.left
            : xPos + width - cellPadding.right;

          doc.text(
            displayValue,
            textX,
            currentY + rowHeight / 2 + 3,
            {
              align: cellStyle.align,
              baseline: 'middle',
              maxWidth: width - (cellPadding.left + cellPadding.right) // Prevent text overflow
            }
          );

          // Draw cell borders
          const cellBorder = cell.border || cellBorders[`${rowIndex},${colIndex}`];
          if (cellBorder) {
            doc.setDrawColor(...(cellBorder.color || [0, 0, 0]));
            if (cellBorder.top) doc.line(xPos, currentY, xPos + width, currentY);
            if (cellBorder.right) doc.line(xPos + width, currentY, xPos + width, currentY + rowHeight);
            if (cellBorder.bottom) doc.line(xPos, currentY + rowHeight, xPos + width, currentY + rowHeight);
            if (cellBorder.left) doc.line(xPos, currentY, xPos, currentY + rowHeight);
          }
        });

        // Draw horizontal inner borders
        if (borderStyle.innerH && !isLastRow) {
          doc.setDrawColor(200, 200, 200);
          doc.line(startX, currentY + rowHeight, startX + tableWidth, currentY + rowHeight);
        }

        // Reset font weight if we changed it
        if (isLastRow && boldLastRow) {
          doc.setFont('helvetica', 'normal');
        }
      });

      // Draw outer borders
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);

      const totalHeight = (data.length + (showHeader ? 1 : 0)) * rowHeight;

      if (borderStyle.top) {
        doc.line(startX, startY, startX + tableWidth, startY);
      }
      if (borderStyle.right) {
        doc.line(startX + tableWidth, startY, startX + tableWidth, startY + totalHeight);
      }
      if (borderStyle.bottom) {
        doc.line(startX, startY + totalHeight, startX + tableWidth, startY + totalHeight);
      }
      if (borderStyle.left) {
        doc.line(startX, startY, startX, startY + totalHeight);
      }

      // Return the Y position after the table with some padding
      return startY + totalHeight + (options.padding?.bottom || 10);
    } catch (error) {
      console.error('Error in addSummaryTable:', error);
      return y;
    }
  },

  /**
   * Add footer to the PDF
   * @param {Object} doc - jsPDF document instance
   */
  addFooter(doc) {
    const pageCount = doc.internal.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      // Page number
      doc.setFontSize(8);
      doc.text(
        `Halaman ${i} dari ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
  }
};

export default printPdfRingkasanPenjualan;